/**
 * Ore e costi delle persone (2.42, ADR-025): who works on a commessa or a
 * progetto, with the hours planned, and the hours worked. Until K-Presenze is
 * linked the hours are written by hand, for a day or as the total of a period;
 * then they will also come from K-Presenze, read only, and the hand-written
 * ones stay possible.
 *
 * The cost is hours × €/ora: for an employee the €/ora of the year from the
 * payroll loaded (the year before when that year has none), for a collaborator
 * the agreement in force that day. The amounts go only to who has the feature
 * «Costi delle persone».
 */
import { z } from 'zod';
import { fromCents, normaliseDecimal, toCents } from './money.js';
import { idSchema, isoDateSchema, optionalText } from './validation.js';
export const HOURS_SOURCES = ['manuale', 'k-presenze'];
export const HOURS_SOURCE_LABELS = {
    manuale: 'A mano',
    'k-presenze': 'K-Presenze',
};
/** Hours: «7,5», «120»; up to two decimals, more than zero. */
const hoursText = (max) => z
    .string()
    .trim()
    .transform((value, context) => {
    const normalised = normaliseDecimal(value);
    const number = normalised === null ? NaN : Number(normalised);
    if (!/^\d{1,5}(\.\d{1,2})?$/.test(normalised ?? '') || !(number > 0) || number > max) {
        context.addIssue({ code: 'custom', message: 'Ore non valide' });
        return z.NEVER;
    }
    return fromCents(Math.round(number * 100));
});
export const jobResourcesSchema = z
    .object({
    people: z
        .array(z.object({
        employeeId: idSchema,
        /** What they do in it: «Contenuti», «Sviluppo». */
        role: optionalText(80),
        plannedHours: hoursText(20000).nullable().default(null),
    }))
        .max(100),
})
    .superRefine((body, context) => {
    const seen = new Set();
    body.people.forEach((person, index) => {
        if (seen.has(person.employeeId)) {
            context.addIssue({
                code: 'custom',
                path: ['people', index, 'employeeId'],
                message: 'Persona già nell’elenco',
            });
        }
        seen.add(person.employeeId);
    });
});
export const jobHoursSchema = z
    .object({
    employeeId: idSchema,
    /** A day, or the first day of the period of a total. */
    fromOn: isoDateSchema,
    /** The last day of the period; the same day for a single day. */
    toOn: isoDateSchema,
    hours: hoursText(5000),
    note: optionalText(300),
})
    .refine((body) => body.toOn >= body.fromOn, {
    path: ['toOn'],
    message: 'La fine viene dopo l’inizio',
});
/** hours × €/ora, to the cent. */
export function hoursCost(hours, hourly) {
    return fromCents(Math.round((toCents(hours) * toCents(hourly)) / 100));
}
/** Whether two periods share at least a day. */
export function periodsOverlap(a, b) {
    return a.fromOn <= b.toOn && b.fromOn <= a.toOn;
}
/**
 * The €/ora of an employee for a day: that of its year, or of the year before
 * when the payroll of that year is not loaded yet; null without either.
 */
export function employeeHourlyOn(byYear, day) {
    const year = Number(day.slice(0, 4));
    return byYear.get(year) ?? byYear.get(year - 1) ?? null;
}
//# sourceMappingURL=job-hours.js.map